/**
 * Configuration globale de la pagination pour le back-office
 */

export const PAGINATION_CONFIG = {
  // Nombre d'éléments par page par défaut
  DEFAULT_PAGE_SIZE: 10,
  
  // Options disponibles pour le nombre d'éléments par page
  PAGE_SIZE_OPTIONS: [5, 10, 20, 50, 100],
  
  // Nombre maximum de boutons de page à afficher
  MAX_PAGE_BUTTONS: 5,
  
  // Configuration par type de page
  PAGE_CONFIGS: {
    clients: {
      pageSize: 15,
      enableSizeSelector: true,
      showInfo: true
    },
    prestataires: {
      pageSize: 12,
      enableSizeSelector: true,
      showInfo: true
    },
    publicite: {
      pageSize: 10,
      enableSizeSelector: true,
      showInfo: true
    },
    missions: {
      pageSize: 15,
      enableSizeSelector: true,
      showInfo: true
    },
    signalements: {
      pageSize: 20,
      enableSizeSelector: true,
      showInfo: true
    },
    documents: {
      pageSize: 15,
      enableSizeSelector: true,
      showInfo: true
    },
    users: {
      pageSize: 10,
      enableSizeSelector: true,
      showInfo: true
    },
    support: {
      pageSize: 10,
      enableSizeSelector: true,
      showInfo: true
    },
    vehicules: {
      pageSize: 12,
      enableSizeSelector: false, // Grid layout
      showInfo: false
    },
    abonnements: {
      pageSize: 6,
      enableSizeSelector: false, // Plan cards
      showInfo: false
    }
  }
};

/**
 * Obtient la configuration de pagination pour une page donnée
 * @param {string} pageType - Type de page (clients, publicite, etc.)
 * @returns {object} Configuration de pagination
 */
export const getPaginationConfig = (pageType) => {
  return {
    pageSize: PAGINATION_CONFIG.PAGE_CONFIGS[pageType]?.pageSize || PAGINATION_CONFIG.DEFAULT_PAGE_SIZE,
    enableSizeSelector: PAGINATION_CONFIG.PAGE_CONFIGS[pageType]?.enableSizeSelector ?? true,
    showInfo: PAGINATION_CONFIG.PAGE_CONFIGS[pageType]?.showInfo ?? true,
    maxPageButtons: PAGINATION_CONFIG.MAX_PAGE_BUTTONS,
    pageSizeOptions: PAGINATION_CONFIG.PAGE_SIZE_OPTIONS
  };
};

/**
 * Calcule la pagination pour un ensemble de données
 * @param {Array} data - Données à paginer
 * @param {number} currentPage - Page actuelle (1-indexed)
 * @param {number} pageSize - Nombre d'éléments par page
 * @returns {object} Informations de pagination
 */
export const calculatePagination = (data, currentPage, pageSize) => {
  const totalItems = Array.isArray(data) ? data.length : 0;
  const totalPages = Math.ceil(totalItems / pageSize);
  const validCurrentPage = Math.max(1, Math.min(currentPage, totalPages));
  
  const startIndex = (validCurrentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);
  
  const paginatedData = Array.isArray(data) ? data.slice(startIndex, endIndex) : [];
  
  return {
    data: paginatedData,
    totalItems,
    totalPages,
    currentPage: validCurrentPage,
    pageSize,
    startIndex: startIndex + 1, // 1-indexed for display
    endIndex,
    hasNextPage: validCurrentPage < totalPages,
    hasPreviousPage: validCurrentPage > 1,
    isFirstPage: validCurrentPage === 1,
    isLastPage: validCurrentPage === totalPages
  };
};

/**
 * Génère les numéros de pages à afficher dans la pagination
 * @param {number} currentPage - Page actuelle
 * @param {number} totalPages - Nombre total de pages
 * @param {number} maxButtons - Nombre maximum de boutons à afficher
 * @returns {Array} Tableau des numéros de pages
 */
export const generatePageNumbers = (currentPage, totalPages, maxButtons = 5) => {
  if (totalPages <= maxButtons) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  
  const sideButtons = Math.floor(maxButtons / 2);
  let startPage = Math.max(1, currentPage - sideButtons);
  let endPage = Math.min(totalPages, currentPage + sideButtons);
  
  // Ajuster si on est près du début
  if (currentPage <= sideButtons) {
    endPage = Math.min(totalPages, maxButtons);
  }
  
  // Ajuster si on est près de la fin
  if (currentPage > totalPages - sideButtons) {
    startPage = Math.max(1, totalPages - maxButtons + 1);
  }
  
  return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
};